import { useListContext } from "ra-core";
import { Link } from "react-router";
import { useIsMobile } from "@/hooks/use-mobile";
import { CreateButton } from "@/components/admin/create-button";
import { List } from "@/components/admin/list";
import { ListPagination } from "@/components/admin/list-pagination";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TopToolbar } from "../layout/TopToolbar";
import { LeadEmpty } from "./LeadEmpty";
import { LeadListFilter } from "./LeadListFilter";
import { LEAD_STATUS_CHOICES } from "./leadConsts";

const LeadList = () => (
  <List
    resource="leads"
    perPage={25}
    title={false}
    sort={{ field: "created_at", order: "DESC" }}
    actions={<LeadActions />}
    pagination={<ListPagination />}
  >
    <LeadListLayout />
  </List>
);

const LeadActions = () => (
  <TopToolbar>
    <CreateButton resource="leads" />
  </TopToolbar>
);

const LeadListLayout = () => {
  const { data, isPending, filterValues } = useListContext();
  const isMobile = useIsMobile();
  const hasFilters = filterValues && Object.keys(filterValues).length > 0;

  if (isPending) return null;
  if (!data?.length && !hasFilters) return <LeadEmpty />;

  return (
    <div className="w-full flex flex-col gap-4">
      {isMobile && <LeadListFilter />}
      <div className="w-full flex flex-row gap-8">
        {!isMobile && <LeadListFilter />}
        <div className="flex flex-col flex-1 min-w-0 gap-4">
          <Card className="py-0 overflow-hidden min-w-0">
            <LeadListContent />
          </Card>
        </div>
      </div>
    </div>
  );
};

const primaryEmail = (lead: any) =>
  lead.email_jsonb?.find((e: any) => e?.email)?.email ?? null;

const primaryPhone = (lead: any) =>
  lead.phone_jsonb?.find((p: any) => p?.number)?.number ?? null;

const LeadListContent = () => {
  const { data, isPending } = useListContext();

  if (isPending) return null;
  if (!data || data.length === 0) return <LeadEmpty />;

  return (
    <div className="overflow-x-auto w-full">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b text-xs text-muted-foreground">
            <th className="text-left p-3 font-medium">Lead #</th>
            <th className="text-left p-3 font-medium">Name</th>
            <th className="text-left p-3 font-medium w-40">Account</th>
            <th className="text-left p-3 font-medium">Status</th>
            <th className="text-left p-3 font-medium">Type</th>
            <th className="text-left p-3 font-medium">Source</th>
            <th className="text-left p-3 font-medium">Email</th>
            <th className="text-left p-3 font-medium">Phone</th>
            <th className="text-left p-3 font-medium">Created</th>
          </tr>
        </thead>
        <tbody>
          {data.map((lead: any) => {
            const statusLabel =
              LEAD_STATUS_CHOICES.find((s) => s.id === lead.status)?.name ??
              lead.status;
            const name =
              [lead.first_name, lead.last_name].filter(Boolean).join(" ") ||
              "—";
            return (
              <tr key={lead.id} className="border-b hover:bg-muted/50">
                <td className="p-3 whitespace-nowrap">
                  <Link
                    to={`/leads/${lead.id}/show`}
                    className="font-medium text-primary hover:underline"
                  >
                    {lead.lead_code || "—"}
                  </Link>
                </td>
                <td className="p-3 max-w-40 truncate" title={name}>
                  {name}
                </td>
                <td
                  className="p-3 max-w-40 truncate"
                  title={lead.account_name || undefined}
                >
                  {lead.account_name || "—"}
                </td>
                <td className="p-3 whitespace-nowrap">
                  {statusLabel ? (
                    <Badge variant="outline">{statusLabel}</Badge>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="p-3 whitespace-nowrap">
                  {lead.lead_type || "—"}
                </td>
                <td className="p-3 max-w-36 truncate">
                  {lead.lead_source || "—"}
                </td>
                <td className="p-3 max-w-44 truncate">
                  {primaryEmail(lead) || "—"}
                </td>
                <td className="p-3 whitespace-nowrap">
                  {primaryPhone(lead) || "—"}
                </td>
                <td className="p-3 whitespace-nowrap">
                  {lead.created_at
                    ? new Date(lead.created_at).toLocaleDateString()
                    : "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export { LeadList };
export default LeadList;
